import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { useDisclosure } from "@nextui-org/modal";
import ClipboardJS from "clipboard";
import clsx from "clsx";
import { useEffect } from "react";
import { z } from "zod";

import { AnalyticsIcon, BoxUSDIcon, ReferralsIcon, TrendingIcon } from "@/components/icons";
import { LicenseSaleTreasury } from "@/components/sui/LicenseSaleTreasury/LicenseSaleTreasury";
import { OwnedLicenses } from "@/components/sui/OwnedLicenses/OwnedLicenses";
import { OwnedReferrer } from "@/components/sui/OwnedReferrer/OwnedReferrer";
import { useClaimsContext } from "@/context/claims/useClaimsContext";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useRewardsInfo } from "@/hooks/useRewardsInfo";
import { useRoutes } from "@/hooks/useRoutes/useRoutes";
import currency from "@/lib/currency";
import { Button } from "@/ui/nextui/Button/Button";
import { Card } from "@/ui/nextui/Card/Card";
import { useToast } from "@/ui/shadcn/use-toast";

import { DailyRewardsCard } from "../DailyRewardsCard/DailyRewardsCard";
import { PurchaseLicenseForm } from "../form/PurchaseLicenseForm/PurchaseLicenseForm";
import { LicenseLeases } from "../LicenseLeases/LicenseLeases";
import { GetAssignCustomReferralCodeFunction } from "../sui/GetAssignCustomReferralCodeFunction/GetAssignCustomReferralCodeFunction";
import { OwnedTransactionsForPackage } from "../sui/OwnedTransactionsForPackage/OwnedTransactionsForPackage";
import { AppModal } from "../ui/AppModal";
import { DashboardProps, PurchaseLicenseFormSchema } from "./Dashboard.types";

export const Dashboard: React.FC<DashboardProps> = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    purchaseLicense,
    currentAccountCoins,
    ownedLicenses,
    ownedReferrer,
    purchaseLicenseForm,
    treasury,
    refetchKnownQueries,
  } = useSuiQueryContext();

  const routes = useRoutes();

  const { toast } = useToast();

  const account = useCurrentAccount();

  const { data: rewardsData } = useRewardsInfo(account?.address);
  const { calculateUnlockedRewardsResponse, calculateUnlockedRewards } = useClaimsContext();

  function onClickToggleBuyNowModal() {
    onOpen();
  }

  function onSubmit(values: z.infer<typeof PurchaseLicenseFormSchema>) {
    // @TODO Display an error if the current account has no coin balance
    if (!currentAccountCoins.coinWithHighestBalance?.objectId) return;

    const referralCode = values.referralCode;

    purchaseLicense(
      {
        referralCode: referralCode || "",
        quantity: Number(values.quantity),
        coinObjectId: currentAccountCoins.coinWithHighestBalance.objectId,
      },
      {
        onSuccessCallback: () => {
          onClose();
          localStorage.setItem("lastLicensePurchaseTime", Date.now().toString());
          setTimeout(() => {
            refetchKnownQueries();
          }, 1000);
        },
      },
    );
  }

  useEffect(() => {
    if (!account?.address) return;

    calculateUnlockedRewards({ user_address: account.address });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.address]);

  useEffect(() => {
    const cb = new ClipboardJS("#copy-referral-code-btn");

    cb.on("success", (e) => {
      toast({
        title: "Referral Code Link Copied!",
        description: `Your referral code link ${e.text} was copied to your clipboard.`,
      });
    });
  }, []);

  return (
    <>
      <OwnedLicenses />
      <OwnedReferrer />
      <LicenseSaleTreasury />
      <GetAssignCustomReferralCodeFunction />

      <section className="py-[180px]" id="dashboard">
        <div className="container mx-auto" id="dashboard-container">
          <div className="mb-12 sm:mb-20" id="dashboard-heading">
            <h1 className="text-4xl uppercase" id="dashboard-title">
              Dashboard
            </h1>
          </div>

          {/* The draw-window score card (`SeasonProgress`) that sat here was removed
              on 2026-09-28 with the draws. It rendered nothing outside a draw
              window, so only its empty `mb-8` wrapper's gap is gone. */}
          <div className="sm:flex sm:gap-12" id="dashboard-main-layout">
            <div className="w-full sm:w-8/12" id="dashboard-primary-column">
              <div className="mb-12 flex flex-col gap-8 sm:flex-row" id="dashboard-summary-row">
                <div className="w-full sm:w-4/12" id="dashboard-agent-licenses-col">
                  <Card
                    className="h-full border-1 border-gray-800 bg-background pb-0"
                    id="dashboard-agent-licenses-card"
                  >
                    <Card.Header id="dashboard-agent-licenses-card-header">
                      <h4 className="uppercase" id="dashboard-agent-licenses-card-title">
                        Agent Licenses
                      </h4>
                    </Card.Header>
                    <Card.Body id="dashboard-agent-licenses-card-body">
                      <h2 className="mb-0 text-gray-400" id="dashboard-agent-licenses-active-count">
                        {ownedLicenses.airdropsCount + ownedLicenses.salesCount} Active
                      </h2>
                      <p className="text-gray-500" id="dashboard-agent-licenses-active-label">
                        Activated Licenses
                      </p>
                    </Card.Body>
                    <Card.Footer
                      className="block overflow-visible px-0 pb-0"
                      id="dashboard-agent-licenses-card-footer"
                    >
                      <div className="-mx-4" id="dashboard-agent-licenses-buy-now-wrapper">
                        <Button
                          fullWidth
                          className="rounded-none"
                          color={treasury.isPurchasingPaused ? "default" : "success"}
                          disabled={treasury.isPurchasingPaused}
                          id="dashboard-agent-licenses-buy-now-button"
                          size="lg"
                          type="button"
                          onClick={onClickToggleBuyNowModal}
                        >
                          {treasury.isPurchasingPaused ? "Purchasing Is Disabled" : "Buy Now"}
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                </div>
                <div className="w-full sm:w-8/12" id="dashboard-referrals-col">
                  {ownedReferrer?.code && (
                    <Card
                      className="h-full border-1 border-gray-800 bg-background pb-0"
                      id="dashboard-referrals-card"
                    >
                      <Card.Header id="dashboard-referrals-card-header">
                        <h4 className="uppercase" id="dashboard-referrals-card-title">
                          Your Referrals
                        </h4>
                      </Card.Header>
                      <Card.Body className="flex flex-row gap-8" id="dashboard-referrals-card-body">
                        <div className="w-7/12" id="dashboard-referrals-code-panel">
                          <div className="flex items-center" id="dashboard-referral-code-row">
                            <h2
                              className="mb-0 mr-2 text-gray-400"
                              id="dashboard-referral-code-value"
                            >
                              {ownedReferrer.code}
                            </h2>
                            <Button
                              isIconOnly
                              data-clipboard-text={routes.referrals.shareLink({
                                code: ownedReferrer.code ?? "",
                              })}
                              id="copy-referral-code-btn"
                              type="button"
                              variant="light"
                            >
                              <ReferralsIcon
                                height={40}
                                id="dashboard-referral-code-copy-icon"
                                width={40}
                              />
                            </Button>
                          </div>
                          <p className="text-gray-500" id="dashboard-referral-code-label">
                            Your Unique Referral Code
                          </p>
                          <div className="mb-1 flex gap-2" id="dashboard-rebate-level-meter">
                            {Array.from(
                              Array(ownedReferrer.rebateLevel.maxReferralsPerTier).keys(),
                            ).map((x, i) => (
                              <div
                                key={`rebate-${i}`}
                                className={clsx("h-4 w-6 bg-white", {
                                  ["!bg-green-500"]:
                                    i < Math.floor(ownedReferrer.rebateLevel.value),
                                })}
                                id={`dashboard-rebate-level-segment-${i}`}
                              />
                            ))}
                          </div>
                          <p className="text-gray-500" id="dashboard-rebate-level-label">
                            Rebate Level {ownedReferrer.rebateLevel.text}
                            <br />
                            <span className="text-xs" id="dashboard-rebate-level-hint">
                              Earn up to 10% cashback on each sale!
                            </span>
                          </p>
                        </div>
                        <div
                          className="flex w-5/12 items-end justify-between pb-8"
                          id="dashboard-referrals-stats-panel"
                        >
                          <div id="dashboard-referrals-count-stat">
                            <h5 className="mb-0" id="dashboard-referrals-count-value">
                              {ownedReferrer.sales}
                            </h5>
                            <p className="mb-0 text-gray-500" id="dashboard-referrals-count-label">
                              Referrals
                            </p>
                          </div>
                          <div id="dashboard-referrals-rebate-stat">
                            <h5 className="mb-0" id="dashboard-referrals-rebate-value">
                              {ownedReferrer.rebate}
                            </h5>
                            <p className="mb-0 text-gray-500" id="dashboard-referrals-rebate-label">
                              Rebate
                            </p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              </div>

              <div className="mb-14 flex flex-col gap-8 sm:flex-row" id="dashboard-rewards-row">
                <div className="h-full w-full sm:w-6/12" id="dashboard-rewards-overview-col">
                  <div className="flex justify-between" id="dashboard-rewards-overview-heading">
                    <h4 id="dashboard-rewards-overview-title">REWARDS OVERVIEW</h4>
                  </div>
                  <Card
                    omitDecoration
                    className="h-[160px] border-1 border-gray-800 bg-background"
                    id="dashboard-rewards-overview-card"
                  >
                    <Card.Body
                      className="flex h-full items-center"
                      id="dashboard-rewards-overview-card-body"
                    >
                      <div
                        className="grid w-full grid-cols-2 gap-6"
                        id="dashboard-rewards-overview-grid"
                      >
                        <div
                          className="flex items-center gap-3"
                          id="dashboard-total-tokens-change-stat"
                        >
                          <AnalyticsIcon
                            height={32}
                            id="dashboard-total-tokens-change-icon"
                            width={32}
                          />
                          <h6
                            className={clsx("mb-0", {
                              "text-green-500": (rewardsData?.totalTokensChange ?? 0) > 0,
                              "text-red-500": (rewardsData?.totalTokensChange ?? 0) < 0,
                            })}
                            id="dashboard-total-tokens-change-value"
                          >
                            {(rewardsData?.totalTokensChange ?? 0) > 0 ? "+" : ""}
                            {(rewardsData?.totalTokensChange ?? 0).toFixed(1)}%
                          </h6>
                        </div>

                        <div
                          className="flex items-center gap-3"
                          id="dashboard-cashback-change-stat"
                        >
                          <BoxUSDIcon height={32} id="dashboard-cashback-change-icon" width={32} />
                          <h6
                            className={clsx("mb-0", {
                              "text-green-500": (rewardsData?.cashbackChange ?? 0) > 0,
                              "text-red-500": (rewardsData?.cashbackChange ?? 0) < 0,
                            })}
                            id="dashboard-cashback-change-value"
                          >
                            {(rewardsData?.cashbackChange ?? 0) > 0 ? "+" : ""}
                            {(rewardsData?.cashbackChange ?? 0).toFixed(1)}%
                          </h6>
                        </div>

                        <div id="dashboard-total-tokens-stat">
                          <h5 className="mb-0 text-gray-300" id="dashboard-total-tokens-value">
                            {currency.convert.toFormattedDigits(
                              (calculateUnlockedRewardsResponse?.unlockedRewards || 0) +
                                (calculateUnlockedRewardsResponse?.lockedRewards || 0) +
                                (calculateUnlockedRewardsResponse?.totalClaimed || 0),
                            )}
                            <sup className="text-xs" id="dashboard-total-tokens-ticker">
                              &nbsp;TRUTH
                            </sup>
                          </h5>
                          <p
                            className="mb-0 text-xs text-gray-500"
                            id="dashboard-total-tokens-label"
                          >
                            Total Tokens
                          </p>
                        </div>
                        <div id="dashboard-total-rebate-stat">
                          <h5 className="mb-0 text-gray-300" id="dashboard-total-rebate-value">
                            {currency.convert.toFormattedString(
                              currency.convert.toDecimalsPrecisionString(
                                rewardsData?.totalCashback || 0,
                                6,
                              ),
                            )}
                            <sup className="text-xs" id="dashboard-total-rebate-ticker">
                              {currency.constants.DEFAULT_CURRENCY_TICKER}
                            </sup>
                          </h5>
                          <p
                            className="mb-0 text-xs text-gray-500"
                            id="dashboard-total-rebate-label"
                          >
                            Total Rebate
                          </p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
                <div className="h-full w-full sm:w-6/12" id="dashboard-rewards-breakdown-col">
                  <h4 id="dashboard-rewards-breakdown-title">REWARDS BREAKDOWN</h4>
                  <Card
                    omitDecoration
                    className="h-[160px] border-1 border-gray-800 bg-background"
                    id="dashboard-rewards-breakdown-card"
                  >
                    <Card.Body
                      className="flex-row gap-8"
                      id="dashboard-rewards-breakdown-card-body"
                    >
                      <div
                        className="flex w-6/12 flex-col justify-between"
                        id="dashboard-agent-pool-panel"
                      >
                        <div
                          className="flex items-center gap-6"
                          id="dashboard-agent-pool-change-row"
                        >
                          <TrendingIcon
                            height={32}
                            id="dashboard-agent-pool-change-icon"
                            width={32}
                          />
                          <div className="w-6/12" id="dashboard-agent-pool-change-stat">
                            <h6
                              className={clsx("mb-0", {
                                "text-green-500": (rewardsData?.agentPoolChange || 0) > 0,
                                "text-red-500": (rewardsData?.agentPoolChange ?? 0) < 0,
                              })}
                              id="dashboard-agent-pool-change-value"
                            >
                              {(rewardsData?.agentPoolChange ?? 0) > 0 ? "+" : ""}
                              {(rewardsData?.agentPoolChange ?? 0).toFixed(1)}%
                            </h6>
                          </div>
                        </div>
                        <div className="mt-6 flex gap-4" id="dashboard-agent-pool-row">
                          <div id="dashboard-agent-pool-stat">
                            <h5 className="mb-0 text-gray-300" id="dashboard-agent-pool-value">
                              {currency.convert.toFormattedDigits(rewardsData?.agentPool || 0)}
                            </h5>
                            <p
                              className="mb-0 text-xs text-gray-500"
                              id="dashboard-agent-pool-label"
                            >
                              Agent Pool
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className="flex w-6/12 flex-col justify-between"
                        id="dashboard-referral-rewards-panel"
                      >
                        <div
                          className="flex justify-between gap-4"
                          id="dashboard-multiple-rewards-row"
                        >
                          <div id="dashboard-multiple-rewards-stat">
                            <h5
                              className="mb-0 text-gray-300"
                              id="dashboard-multiple-rewards-value"
                            >
                              {currency.convert.toFormattedDigits(
                                rewardsData?.multipleReferralRewards || 0,
                              )}
                            </h5>
                            <p
                              className="mb-0 text-xs text-gray-500"
                              id="dashboard-multiple-rewards-label"
                            >
                              Multiple
                            </p>
                          </div>
                          <div id="dashboard-tier-boost-stat">
                            <h6 className="mb-0 text-yellow-400" id="dashboard-tier-boost-value">
                              {rewardsData?.tierBoost || 0}x
                            </h6>
                            <p
                              className="mb-0 text-xs text-gray-500"
                              id="dashboard-tier-boost-label"
                            >
                              Boost
                            </p>
                          </div>
                        </div>
                        <div
                          className="mt-6 flex justify-between gap-4"
                          id="dashboard-referral-rewards-row"
                        >
                          <div id="dashboard-referral-rewards-stat">
                            <h5
                              className="mb-0 text-gray-300"
                              id="dashboard-referral-rewards-value"
                            >
                              {currency.convert.toFormattedDigits(
                                rewardsData?.referralRewards || 0,
                              )}
                            </h5>
                            <p
                              className="mb-0 text-xs text-gray-500"
                              id="dashboard-referral-rewards-label"
                            >
                              Referral
                            </p>
                          </div>
                          <div id="dashboard-referral-boost-stat">
                            <h6
                              className="mb-0 text-yellow-400"
                              id="dashboard-referral-boost-value"
                            >
                              0.1x
                            </h6>
                            <p
                              className="mb-0 text-xs text-gray-500"
                              id="dashboard-referral-boost-label"
                            >
                              Boost
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </div>
            <div className="w-full sm:w-4/12" id="dashboard-daily-rewards-column">
              <DailyRewardsCard />
            </div>
          </div>

          <div className="mb-14" id="dashboard-license-leases-section">
            <LicenseLeases onClickToggleBuyNowModal={onClickToggleBuyNowModal} />
          </div>

          <div id="dashboard-transaction-history-section">
            <h4 className="uppercase" id="dashboard-transaction-history-title">
              Transaction History
            </h4>
            <OwnedTransactionsForPackage />
          </div>
        </div>
      </section>

      <AppModal
        id="dashboard-purchase-license-modal"
        isOpen={isOpen}
        size="sm"
        title="Agent License"
        onClose={onClose}
      >
        <PurchaseLicenseForm
          className="mb-4 w-full sm:mb-0"
          id="dashboard-purchase-license-form"
          onSubmit={purchaseLicenseForm.handleSubmit(onSubmit)}
        >
          <PurchaseLicenseForm.Body />
          <div className="pt-4" id="dashboard-purchase-license-submit-wrapper">
            <Button
              fullWidth
              color={treasury.isPurchasingPaused ? "default" : "success"}
              disabled={treasury.isPurchasingPaused}
              id="dashboard-purchase-license-submit-button"
              size="lg"
              type="submit"
            >
              {treasury.isPurchasingPaused ? "Purchasing Is Disabled" : "Buy Now"}
            </Button>
          </div>
        </PurchaseLicenseForm>
      </AppModal>
    </>
  );
};
